/**
 * AgentFeedWebSocket — connection manager for the org agent-feed WebSocket channel.
 *
 * Follows the ProposalWebSocket pattern: stateless w.r.t. feed items — just
 * forwards parsed server messages to caller-supplied callbacks; store mutations
 * happen in the hook layer (next phase).
 *
 * Adds inactivity detection (visibilitychange from CrmWebSocket): if the tab has
 * been hidden for more than USER_IDLE_THRESHOLD_MS, fires onInactivityRefresh so
 * callers can do a full REST refresh + replaceAll() instead of relying on stale
 * local state.
 */

import { USER_IDLE_THRESHOLD_MS } from '@/lib/activity/constants';
import { getWebSocketBaseUrl } from '@/lib/config';

import type {
  AgentFeedClientMessage,
  AgentFeedServerMessage,
  AgentFeedWebSocketStatus,
} from './types';

const MIN_RECONNECT_DELAY_MS = 1000;
const MAX_RECONNECT_DELAY_MS = 30000;
const HEARTBEAT_INTERVAL_MS = 30000;
const HEARTBEAT_TIMEOUT_MS = 10000;

function withJitter(delay: number): number {
  const jitter = 0.8 + Math.random() * 0.4;
  return Math.round(delay * jitter);
}

interface AgentFeedWebSocketConfig {
  orgId: number;
  onTriggerPending: (
    msg: Extract<AgentFeedServerMessage, { type: 'trigger.pending' }>
  ) => void;
  onAgentStarted: (
    msg: Extract<AgentFeedServerMessage, { type: 'agent.started' }>
  ) => void;
  onAgentCompleted: (
    msg: Extract<AgentFeedServerMessage, { type: 'agent.completed' }>
  ) => void;
  /** Fired when WS opens after a previous disconnect (NOT on the initial connect). Used to trigger REST refetch to backfill missed events. */
  onReconnected?: () => void;
  /** Fired when the tab becomes visible after >INACTIVITY_THRESHOLD_MS hidden. Used to trigger a full REST refresh + replaceAll(). */
  onInactivityRefresh?: () => void;
  onStatusChange?: (status: AgentFeedWebSocketStatus) => void;
}

export class AgentFeedWebSocket {
  private ws: WebSocket | null = null;
  private config: AgentFeedWebSocketConfig;
  private reconnectAttempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private heartbeatTimeoutTimer: ReturnType<typeof setTimeout> | null = null;
  private hiddenSince: number | null = null;
  private status: AgentFeedWebSocketStatus = 'disconnected';
  private destroyed = false;
  private shouldNotReconnect = false;
  private hasConnectedBefore = false;

  constructor(config: AgentFeedWebSocketConfig) {
    this.config = config;
  }

  connect(): void {
    if (this.destroyed || this.shouldNotReconnect) {
      return;
    }
    this.disconnect();
    this.setStatus('connecting');

    const url = `${getWebSocketBaseUrl()}/ws/orgs/${this.config.orgId}/agent-feed/`;

    try {
      this.ws = new WebSocket(url);
    } catch (err) {
      console.warn(
        '[AgentFeedWebSocket] WebSocket constructor failed, scheduling reconnect:',
        err
      );
      this.setStatus('disconnected');
      this.scheduleReconnect();
      return;
    }

    this.ws.onopen = () => {
      if (this.destroyed) {
        return;
      }
      this.setStatus('connected');
      this.reconnectAttempt = 0;
      if (this.hasConnectedBefore) {
        this.config.onReconnected?.();
      }
      this.hasConnectedBefore = true;
      this.startHeartbeat();
    };

    this.ws.onmessage = event => {
      if (this.destroyed) {
        return;
      }
      // Any inbound message means the server is alive — clear pending timeout.
      if (this.heartbeatTimeoutTimer) {
        clearTimeout(this.heartbeatTimeoutTimer);
        this.heartbeatTimeoutTimer = null;
      }
      try {
        const message = JSON.parse(event.data) as AgentFeedServerMessage;
        this.handleMessage(message);
      } catch {
        console.error(
          '[AgentFeedWebSocket] Failed to parse message:',
          event.data
        );
      }
    };

    this.ws.onclose = closeEvent => {
      if (this.destroyed) {
        return;
      }
      this.stopHeartbeat();
      this.setStatus('disconnected');

      // Auth/permission errors — don't reconnect; we'll just fail forever.
      if (closeEvent.code === 4401 || closeEvent.code === 4403) {
        console.warn(
          `[AgentFeedWebSocket] Connection rejected (code ${closeEvent.code}), not reconnecting`
        );
        this.shouldNotReconnect = true;
        return;
      }

      this.scheduleReconnect();
    };

    this.ws.onerror = () => {
      // onerror is always followed by onclose, where reconnect is scheduled.
    };

    this.setupVisibilityListener();
  }

  disconnect(): void {
    this.clearReconnectTimer();
    this.stopHeartbeat();
    this.removeVisibilityListener();

    if (this.ws) {
      // Drop handlers so the deliberate close doesn't trigger reconnect.
      this.ws.onclose = null;
      this.ws.onerror = null;
      this.ws.onmessage = null;
      this.ws.onopen = null;

      if (
        this.ws.readyState === WebSocket.OPEN ||
        this.ws.readyState === WebSocket.CONNECTING
      ) {
        this.ws.close(1000, 'Client disconnect');
      }
      this.ws = null;
    }

    this.setStatus('disconnected');
  }

  destroy(): void {
    this.destroyed = true;
    this.disconnect();
    this.removeVisibilityListener();
  }

  getStatus(): AgentFeedWebSocketStatus {
    return this.status;
  }

  // --- Internal ---

  private handleMessage(message: AgentFeedServerMessage): void {
    switch (message.type) {
      case 'trigger.pending':
        this.config.onTriggerPending(message);
        break;
      case 'agent.started':
        this.config.onAgentStarted(message);
        break;
      case 'agent.completed':
        this.config.onAgentCompleted(message);
        break;
      case 'pong':
        break;
    }
  }

  private send(message: AgentFeedClientMessage): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    }
  }

  private setStatus(status: AgentFeedWebSocketStatus): void {
    if (this.status !== status) {
      this.status = status;
      this.config.onStatusChange?.(status);
    }
  }

  private scheduleReconnect(): void {
    if (this.destroyed || this.shouldNotReconnect) {
      return;
    }
    this.clearReconnectTimer();
    const delay = withJitter(
      Math.min(
        MIN_RECONNECT_DELAY_MS * 2 ** this.reconnectAttempt,
        MAX_RECONNECT_DELAY_MS
      )
    );
    this.reconnectAttempt++;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      this.send({ type: 'ping' });
      // Re-arm the dead-connection timeout. Clearing first prevents leaked
      // timers after sleep/wake when queued timers can fire out of order.
      if (this.heartbeatTimeoutTimer) {
        clearTimeout(this.heartbeatTimeoutTimer);
        this.heartbeatTimeoutTimer = null;
      }
      this.heartbeatTimeoutTimer = setTimeout(() => {
        console.warn('[AgentFeedWebSocket] Heartbeat timeout, reconnecting');
        this.ws?.close();
      }, HEARTBEAT_TIMEOUT_MS);
    }, HEARTBEAT_INTERVAL_MS);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
    if (this.heartbeatTimeoutTimer) {
      clearTimeout(this.heartbeatTimeoutTimer);
      this.heartbeatTimeoutTimer = null;
    }
  }

  private handleVisibilityChange = (): void => {
    if (this.destroyed) {
      return;
    }

    if (document.hidden) {
      this.hiddenSince = Date.now();
    } else {
      // Tab became visible again
      if (this.hiddenSince) {
        const elapsed = Date.now() - this.hiddenSince;
        this.hiddenSince = null;

        // Reuses the shared idle threshold; here it gates the tab-HIDDEN-duration
        // stale-refresh (a distinct policy that happens to share the same 10-min value).
        if (elapsed >= USER_IDLE_THRESHOLD_MS) {
          // Been hidden too long — local state may be very stale.
          // Discard it and refetch everything.
          console.warn(
            '[AgentFeedWebSocket] Tab inactive >10 min, triggering inactivity refresh (org:',
            this.config.orgId,
            ')'
          );
          this.config.onInactivityRefresh?.();
        }
      }

      // If we somehow lost the connection while hidden, reconnect now
      if (
        this.status === 'disconnected' &&
        !this.shouldNotReconnect &&
        !this.destroyed
      ) {
        console.warn(
          '[AgentFeedWebSocket] Reconnecting after tab became visible while disconnected (org:',
          this.config.orgId,
          ')'
        );
        // Cancel any pending reconnect timer — we want to reconnect immediately
        // since the user is actively looking at the page
        this.clearReconnectTimer();
        this.reconnectAttempt = 0; // Reset backoff since user is actively looking
        this.connect();
      }
    }
  };

  private setupVisibilityListener(): void {
    // Remove any existing listener first to make this idempotent
    // (connect() is called on every reconnect)
    this.removeVisibilityListener();
    document.addEventListener('visibilitychange', this.handleVisibilityChange);

    // If we connect (or reconnect) while the tab is ALREADY hidden, no
    // visibilitychange event will fire to set hiddenSince — so when the user
    // later returns, handleVisibilityChange would see hiddenSince === null and
    // skip the >10-min inactivity refresh entirely. Seed it here so the
    // elapsed-time check still works. Preserve any earlier timestamp carried
    // across a reconnect rather than resetting the clock.
    if (document.hidden && this.hiddenSince === null) {
      this.hiddenSince = Date.now();
    }
  }

  private removeVisibilityListener(): void {
    document.removeEventListener(
      'visibilitychange',
      this.handleVisibilityChange
    );
  }
}
